import type React from "react";
import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type { ProposalService } from "../types/proposal.types";
import AddServiceDialog from "./AddServiceDialog";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";

interface ServicesSectionProps {
  selectedServices: ProposalService[];
  onServicesChange: (services: ProposalService[]) => void;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({
  selectedServices,
  onServicesChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const handleAddService = (proposalService: ProposalService) => {
    onServicesChange([...selectedServices, proposalService]);
    setDialogOpen(false);
  };

  const handleRemoveService = (index: number) => {
    const newServices = selectedServices.filter((_, i) => i !== index);
    onServicesChange(newServices);
  };

  const calculateTotal = () => {
    return selectedServices.reduce(
      (sum, ps) => sum + ps?.service?.price * ps?.quantity,
      0
    );
  };

  return (
    <Paper sx={{ p: 3, boxShadow: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Services & Items
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: reduxTheme.primary_colors[400] }}
        >
          Add Service
        </Button>
      </Box>

      {selectedServices.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: "text.secondary",
          }}
        >
          <AddIcon sx={{ fontSize: 64, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No services added yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Start building your proposal by adding services
          </Typography>
          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: reduxTheme.primary_colors[400] }}
          >
            Add First Service
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            {selectedServices.map((ps, index) => (
              <Card key={index} sx={{ mb: 2, boxShadow: 0 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {ps?.service?.name}
                        </Typography>
                        {ps?.service?.customizable && (
                          <Chip
                            label="Customizable"
                            size="small"
                            color="success"
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {ps?.service?.description}
                      </Typography>
                      {ps?.service?.features && (
                        <Box sx={{ mb: 1 }}>
                          {ps?.service?.features?.map((feature, idx) => (
                            <Chip
                              key={idx}
                              label={feature}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                      {ps?.customizations && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, fontStyle: "italic" }}
                        >
                          <strong>Customizations:</strong> {ps?.customizations}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Quantity:</strong> {ps?.quantity}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        ${(ps?.service?.price * ps?.quantity).toLocaleString()}
                      </Typography>
                      <IconButton
                        onClick={() => handleRemoveService(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                ${calculateTotal().toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Tax (8%):</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                ${(calculateTotal() * 0.08).toLocaleString()}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                pt: 1,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ${(calculateTotal() * 1.08).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <AddServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAddService}
      />
    </Paper>
  );
};

export default ServicesSection;
